import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AccountBalanceWallet,
  AccountBalance,
  CreditCard,
  CloudUpload,
  CheckCircle,
  HourglassEmpty,
  Error,
  Business,
} from '@mui/icons-material';
import axios from 'axios';
import TicketService from '../../../services/TicketService';
import AppService from '../../../services/AppService';
import { useTransactionAudit } from '../../../hooks/useTransactionAudit';
import { API_BASE_URL } from '../../../utils/constants';
import { getToken as getAuthToken } from '../../../services/auth';
import { useDispatch } from 'react-redux';
import { showLoader, dismissLoader } from '../../../LayoutContainers/AppLayout/redux/AppSlice';

// Default fallback Thaklis bank accounts (in case API fails)
const DEFAULT_THAKLIS_BANKS = [
  {
    id: 'bank-001',
    accountName: 'Thaklis Main Operations',
    bankName: 'Emirates NBD',
    accountNumber: '****0123',
    iban: 'AE070331234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: true,
  },
  {
    id: 'bank-002',
    accountName: 'Thaklis Business Account',
    bankName: 'Abu Dhabi Commercial Bank',
    accountNumber: '****5678',
    iban: 'AE330321987654321098765',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
  {
    id: 'bank-003',
    accountName: 'Thaklis Shariah Compliant',
    bankName: 'Dubai Islamic Bank',
    accountNumber: '****9012',
    iban: 'AE440401234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function Payments() {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [customerId, setCustomerId] = useState(null);
  const [unpaidTickets, setUnpaidTickets] = useState([]);
  // Server-pagination state for the Pay-for-Tickets unpaid list. Total count
  // and total AED amount come from the server so the header tile remains
  // accurate independent of the page slice.
  const [unpaidPage, setUnpaidPage] = useState(0);
  const [unpaidPageSize, setUnpaidPageSize] = useState(20);
  const [unpaidTotal, setUnpaidTotal] = useState(0);
  const [unpaidTotalAmount, setUnpaidTotalAmount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerPaymentMethods, setCustomerPaymentMethods] = useState([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);

  // Dedicated endpoint state for Transaction History tab
  const [txnTopups, setTxnTopups] = useState([]);
  const [txnTicketPayments, setTxnTicketPayments] = useState([]);
  const [txnTopupsLoading, setTxnTopupsLoading] = useState(false);
  const [txnTicketPaymentsLoading, setTxnTicketPaymentsLoading] = useState(false);

  // Bank accounts state
  const [thaklisBankAccounts, setThaklisBankAccounts] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);

  // Top-up Wallet states
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPaymentType, setTopupPaymentType] = useState('online'); // online or offline
  const [topupPaymentMethod, setTopupPaymentMethod] = useState('ngenius'); // for online: ngenius (integrated), stripe/tabby/noqudi (coming soon)
  const [topupOfflineMethodId, setTopupOfflineMethodId] = useState(''); // for offline - selected from customer's methods
  const [topupProofFile, setTopupProofFile] = useState(null);
  const [topupTransactionDate, setTopupTransactionDate] = useState(null);
  const [topupDestinationBankId, setTopupDestinationBankId] = useState(''); // Destination bank selection for offline payments
  const [showTopupDialog, setShowTopupDialog] = useState(false);

  // Pay for Tickets states
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [payTicketsPaymentType, setPayTicketsPaymentType] = useState('wallet');
  const [payTicketsPaymentMethod, setPayTicketsPaymentMethod] = useState('ngenius');
  const [showPayTicketsDialog, setShowPayTicketsDialog] = useState(false);

  // Ticket History states — server-paginated, server-filtered. The previous
  // client-side filter + slice over the full ticket list is gone.
  const [allTickets, setAllTickets] = useState([]);
  const [ticketHistoryTotal, setTicketHistoryTotal] = useState(0);
  const [ticketHistoryLoading, setTicketHistoryLoading] = useState(false);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('ALL');
  const [ticketHistoryPage, setTicketHistoryPage] = useState(0);
  const [ticketHistoryPageSize, setTicketHistoryPageSize] = useState(20);

  // Payment History states (wallet topups)
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState({
    totalApprovedAmount: 0,
    totalPendingAmount: 0,
    totalRejectedAmount: 0,
    approvedCount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    currentWalletBalance: 0,
    availableBalance: 0
  });

  // NGenius platform-fee config (percentage + fixed) for the online-payment surcharge preview.
  const [ngeniusFee, setNgeniusFee] = useState({ feePercentage: 0, feeFixed: 0 });

  // Platform-fee surcharge for an online amount. Mirrors the backend's
  // calculatePlatformFee exactly: round the percentage part to 2dp first, then add
  // the fixed fee (HALF_UP). Preview only — the backend recomputes on submit.
  const computePlatformFee = (amount) => {
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) return 0;
    const pctPart = Math.round((amt * (ngeniusFee.feePercentage || 0)) / 100 * 100) / 100;
    return Math.round((pctPart + (ngeniusFee.feeFixed || 0)) * 100) / 100;
  };

  // Transaction History sub-tab state
  const [transactionHistorySubTab, setTransactionHistorySubTab] = useState(0);

  // General states
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Top-up proof extraction UI states (keep error local to form so user doesn't need to scroll to top)
  const [topupProofExtractionError, setTopupProofExtractionError] = useState(null);
  const [topupProofExtractionLoading, setTopupProofExtractionLoading] = useState(false);
  // Amount auto-extracted from the proof receipt. Held separately from the editable
  // topupAmount field so we can compare on submit and flag mismatches for approval review.
  const [topupExtractedAmount, setTopupExtractedAmount] = useState(null);
  const [amountMismatchDialogOpen, setAmountMismatchDialogOpen] = useState(false);

  // Global loader reference counter to support overlapping API calls (same pattern as EmployeeCreate)
  const globalLoaderCount = useRef(0);
  const startGlobalLoader = useCallback(() => {
    globalLoaderCount.current += 1;
    if (globalLoaderCount.current === 1) {
      dispatch(showLoader());
    }
  }, [dispatch]);

  const stopGlobalLoader = useCallback(() => {
    globalLoaderCount.current = Math.max(0, globalLoaderCount.current - 1);
    if (globalLoaderCount.current === 0) {
      dispatch(dismissLoader());
    }
  }, [dispatch]);

  // Load the NGenius platform-fee config for the online-payment surcharge preview.
  useEffect(() => {
    let active = true;
    AppService.get('v1/payment-gateways/enabled')
      .then((list) => {
        if (!active) return;
        const ng = (Array.isArray(list) ? list : []).find((g) => g.gatewayKey === 'NGENIUS');
        if (ng) {
          setNgeniusFee({
            feePercentage: Number(ng.feePercentage) || 0,
            feeFixed: Number(ng.feeFixed) || 0,
          });
        }
      })
      .catch(() => { /* no config → no fee shown */ });
    return () => { active = false; };
  }, []);

  // Extract customerId from localStorage on mount
  useEffect(() => {
    console.log("\n╔════════════════════════════════════════╗");
    console.log("║   Payments Component - INITIALIZE     ║");
    console.log("╚════════════════════════════════════════╝");
    try {
      const authInfo = localStorage.getItem('authInfo');
      console.log('[Payments] authInfo retrieved from localStorage:', !!authInfo);
      if (authInfo) {
        const parsed = JSON.parse(authInfo);
        console.log('[Payments] Parsed authInfo - user:', parsed?.user?.id);
        console.log('[Payments] Parsed authInfo - organizationCode:', parsed?.organizationCode);
        console.log('[Payments] Parsed authInfo - organisationId:', parsed?.principalInfo?.organisationId);
        const cId = parsed?.user?.id || parsed?.organizationCode || parsed?.principalInfo?.organisationId;
        setCustomerId(cId);
        console.log('[Payments] CustomerId extracted:', cId);
      }
    } catch (error) {
      console.error('[Payments] Error extracting customerId:', error);
    }
  }, []);

  // Fetch organization ID from wallet balance API using AppService
  useEffect(() => {
    const fetchOrganizationIdAndSummary = async () => {
      try {
        console.log('[Payments] Fetching organization ID from wallet balance...');
        const walletInfo = await AppService.get('v1/wallet/transactions/balance');

        console.log('[Payments] Wallet info response:', walletInfo);
        if (walletInfo && walletInfo.organisationId) {
          console.log('[Payments] Organization ID fetched:', walletInfo.organisationId);
          setOrganizationId(walletInfo.organisationId);
          setWalletBalance(walletInfo.balance || 0);
        }

        // Load payment summary on mount
        console.log('[Payments] Loading payment summary on mount...');
        const summaryResponse = await TicketService.getPaymentSummary();
        console.log('[Payments] Payment summary loaded:', summaryResponse);
        if (summaryResponse) {
          setPaymentSummary(summaryResponse);
        }
      } catch (error) {
        console.error('[Payments] Error fetching organization ID or summary:', error);
      }
    };

    fetchOrganizationIdAndSummary();
  }, []);

  // Load unpaid tickets, customer payment methods, and available payment methods when customerId is available
  useEffect(() => {
    console.log("\n[Payments] ===== useEffect [customerId] TRIGGERED =====");
    console.log("[Payments] customerId value:", customerId);
    console.log("[Payments] customerId is truthy:", !!customerId);

    // Load available payment methods and customer's payment methods immediately (they use authenticated endpoints)
    console.log('[Payments] Calling loadCustomerPaymentMethods...');
    loadCustomerPaymentMethods();
    console.log('[Payments] Calling loadAvailablePaymentMethods...');
    loadAvailablePaymentMethods();

    // Only load unpaid tickets if customerId is available (this endpoint might need it)
    if (customerId) {
      console.log("[Payments] ✓ customerId is available, calling loadUnpaidTickets...");
      loadUnpaidTickets();
    } else {
      console.log("[Payments] ✗ customerId is NOT available, skipping loadUnpaidTickets");
    }
    console.log("[Payments] ===== END useEffect [customerId] =====\n");
  }, [customerId]);

  // Fetch available bank accounts from API
  useEffect(() => {
    const fetchThaklisBankAccounts = async () => {
      try {
        setBanksLoading(true);
        console.log('[Payments] Fetching available bank accounts from API...');

        // Call API to get available banks for current customer
        // Returns BankMappingWithDetailsDTO which includes bankName, accountNumber, iban, etc.
        const response = await AppService.get('/v1/bank-accounts/customer/available');

        if (response && Array.isArray(response)) {
          console.log('[Payments] ✓ Bank accounts fetched successfully:', response);

          // Transform API response to match component expectations
          // API returns: { bankName, accountNumber, iban, accountName, isDefault, swiftCode, type, ... }
          const transformedBanks = response.map((bank, index) => ({
            id: bank.bankAccountId || `bank-${index}`,
            accountName: bank.accountName,
            bankName: bank.bankName,
            accountNumber: bank.accountNumber,
            iban: bank.iban,
            currency: 'AED', // Default to AED
            isActive: bank.status === 'ACTIVE',
            isPrimary: bank.isDefault || index === 0,
          }));

          setThaklisBankAccounts(transformedBanks);

          // Set default selected bank to primary or first available
          const primaryBank = transformedBanks.find(bank => bank.isPrimary);
          if (primaryBank) {
            setTopupDestinationBankId(primaryBank.id);
          } else if (transformedBanks.length > 0) {
            setTopupDestinationBankId(transformedBanks[0].id);
          }
        } else {
          throw new Error('No data in API response');
        }
      } catch (error) {
        console.warn('[Payments] ⚠ Failed to fetch bank accounts from API, using fallback data:', error.message);
        // Use default fallback banks if API fails
        setThaklisBankAccounts(DEFAULT_THAKLIS_BANKS);
        const primaryBank = DEFAULT_THAKLIS_BANKS.find(bank => bank.isPrimary);
        if (primaryBank) {
          setTopupDestinationBankId(primaryBank.id);
        }
      } finally {
        setBanksLoading(false);
      }
    };

    fetchThaklisBankAccounts();
  }, []); // Run once on component mount

  useEffect(() => {
    if (topupPaymentType !== 'offline') return;
    if (topupPaymentMethod) return;
    if (!availablePaymentMethods || availablePaymentMethods.length === 0) return;
    setTopupPaymentMethod(availablePaymentMethods[0].id);
  }, [topupPaymentType, topupPaymentMethod, availablePaymentMethods]);


  const loadCustomerPaymentMethods = async () => {
    try {
      console.log('[Payments] Loading customer payment methods...');
      const methods = await TicketService.getCustomerPaymentMethods(customerId);
      console.log('[Payments] Customer payment methods loaded successfully:', methods);
      console.log('[Payments] Methods count:', Array.isArray(methods) ? methods.length : 0);
      setCustomerPaymentMethods(Array.isArray(methods) ? methods : []);
    } catch (error) {
      console.error('[Payments] Error loading payment methods:', error);
      setCustomerPaymentMethods([]);
    }
  };

  const loadUnpaidTickets = async (overrides = {}) => {
    const page = overrides.page !== undefined ? overrides.page : unpaidPage;
    const pageSize = overrides.pageSize !== undefined ? overrides.pageSize : unpaidPageSize;
    setLoading(true);
    try {
      const response = await TicketService.getUnpaidTickets(customerId, {
        page,
        pageSize,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      const total = Number(response?.totalElements) || 0;
      const totalAmount = Number(response?.totalUnpaidAmount) || 0;
      setUnpaidTickets(items);
      setUnpaidTotal(total);
      setUnpaidTotalAmount(totalAmount);
    } catch (error) {
      console.error('[Payments] Error loading unpaid tickets:', error);
      if (error?.response?.status === 403) {
        // Partner users don't have access — not an error.
        setUnpaidTickets([]);
        setUnpaidTotal(0);
        setUnpaidTotalAmount(0);
      } else {
        setErrorMessage('Failed to load unpaid tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAllTickets = async (overrides = {}) => {
    const page = overrides.page !== undefined ? overrides.page : ticketHistoryPage;
    const pageSize = overrides.pageSize !== undefined ? overrides.pageSize : ticketHistoryPageSize;
    const status = overrides.status !== undefined ? overrides.status : ticketStatusFilter;
    setTicketHistoryLoading(true);
    try {
      const response = await TicketService.getAllTickets(customerId, {
        page,
        pageSize,
        paymentStatus: status,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      const total = Number(response?.totalElements) || 0;
      setAllTickets(items);
      setTicketHistoryTotal(total);
    } catch (error) {
      console.error('[Payments] Error loading all tickets:', error);
      setAllTickets([]);
      setTicketHistoryTotal(0);
      setErrorMessage('Failed to load ticket history');
    } finally {
      setTicketHistoryLoading(false);
    }
  };

  // Server returns the filtered/paged slice already — no client-side filter
  // or slice. allTickets IS the current page's contents.
  const filteredTickets = allTickets;
  const totalPages = Math.max(1, Math.ceil(ticketHistoryTotal / ticketHistoryPageSize));
  const paginatedTickets = allTickets;

  const loadAvailablePaymentMethods = async () => {
    try {
      const methods = await TicketService.getAvailablePaymentMethods();
      console.log('[Payments] Available payment methods loaded:', methods);
      setAvailablePaymentMethods(Array.isArray(methods) ? methods : []);
    } catch (error) {
      console.error('[Payments] Error loading available payment methods:', error);
      setAvailablePaymentMethods([]);
    }
  };

  // Fetch dedicated endpoints when Transaction History tab (tab 3) is selected
  useEffect(() => {
    if (tabValue === 3) {
      console.log('[Payments] Transaction History tab selected, fetching from dedicated endpoints...');

      // Fetch topups
      setTxnTopupsLoading(true);
      AppService.get('v1/wallet/transactions/topups')
        .then(res => {
          const data = Array.isArray(res) ? res : res?.data || [];
          setTxnTopups(data);
        })
        .catch(err => {
          console.error('[Payments] Failed to fetch topups:', err);
          setTxnTopups([]);
        })
        .finally(() => setTxnTopupsLoading(false));

      // Fetch ticket payments (unified: wallet + online)
      setTxnTicketPaymentsLoading(true);
      AppService.get('v1/wallet/transactions/ticket-payment')
        .then(res => {
          const data = Array.isArray(res) ? res : res?.data || [];
          setTxnTicketPayments(data);
        })
        .catch(err => {
          console.error('[Payments] Failed to fetch ticket payments:', err);
          setTxnTicketPayments([]);
        })
        .finally(() => setTxnTicketPaymentsLoading(false));
    }
  }, [tabValue]);

  const formatAmount = (amount) => {
    const value = Number(amount) || 0;
    return `AED ${value.toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleSelectAllUnpaidTickets = (event) => {
    if (event.target.checked) {
      setSelectedTickets(unpaidTickets.map(t => t.ticketId));
    } else {
      setSelectedTickets([]);
    }
  };

  const handleTicketToggle = (ticketId) => {
    setSelectedTickets(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const calculateTotalAmount = () => {
    return selectedTickets.reduce((sum, ticketId) => {
      const ticket = unpaidTickets.find(t => t.ticketId === ticketId);
      return sum + (ticket?.totalCost || 0);
    }, 0);
  };

  const toLocalDateString = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseLocalDateString = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-').map((p) => parseInt(p, 10));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
  };

  const extractTopupProofData = async (file) => {
    if (!file) return;

    try {
      setTopupProofExtractionError(null);
      setTopupProofExtractionLoading(true);
      startGlobalLoader();

      const token = getAuthToken();
      if (!token) {
        setTopupProofExtractionError('Unable to extract payment proof: authentication required. Please login again.');
        return;
      }

      const MAX_RETRIES = 3;
      const RETRY_DELAY = 2000;

      let successPayload = null;
      let lastError = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(
            `${API_BASE_URL}/v1/documents/processing/topup-proof/extract`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
              timeout: 60000,
            }
          );

          const payload = response?.data ?? response;

          // Some backends return a 200 with an error payload
          if (payload && payload.code && String(payload.code).includes('ERROR')) {
            throw new Error(payload.message || 'Processing error');
          }

          successPayload = payload;
          break;
        } catch (err) {
          lastError = err;
          console.warn(`[Payments] Topup proof extraction attempt ${attempt} failed:`, err?.message || err);
          if (attempt < MAX_RETRIES) {
            await new Promise((res) => setTimeout(res, RETRY_DELAY));
          }
        }
      }

      if (!successPayload) {
        throw lastError || new Error('Extraction failed');
      }

      const payload = successPayload;
      const extractedAmount = payload?.amount;
      const extractedDate = payload?.date;

      if (extractedAmount) {
        setTopupAmount(String(extractedAmount));
        // Stash the extracted value separately so handleTopupConfirm can
        // compare it against whatever the user finally submits.
        setTopupExtractedAmount(String(extractedAmount));
      } else {
        setTopupExtractedAmount(null);
      }

      if (extractedDate) {
        const parsedDate = parseLocalDateString(String(extractedDate));
        if (parsedDate) {
          setTopupTransactionDate(parsedDate);
        }
      }

      const amountOk = Boolean(extractedAmount);
      const dateOk = Boolean(extractedDate) && Boolean(parseLocalDateString(String(extractedDate)));

      if (!amountOk || !dateOk) {
        setTopupProofExtractionError(
          `Auto-fill failed: ${!amountOk ? 'Amount' : ''}${!amountOk && !dateOk ? ' and ' : ''}${!dateOk ? 'Transaction date' : ''} could not be extracted. Please enter manually.`
        );
      } else {
        setTopupProofExtractionError(null);
      }
    } catch (err) {
      console.error('[Payments] Error extracting topup proof:', err);
      setTopupProofExtractionError('Failed to automatically fill amount and transaction date from payment proof. Please enter them manually.');
    } finally {
      setTopupProofExtractionLoading(false);
      stopGlobalLoader();
    }
  };

  const handleTopupProofFileChange = async (event) => {
    const file = event.target.files?.[0] || null;
    setTopupProofFile(file);
    if (file) {
      await extractTopupProofData(file);
    }
  };

  // `confirmedMismatch` is set to true only after the user explicitly clicks
  // "Continue" on the amount-mismatch confirmation dialog. On the first pass
  // we open the dialog and bail; on the second pass we mark the topup with
  // tags so admins can spot it on the approvals page.
  const handleTopupConfirm = async (confirmedMismatch = false) => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      setErrorMessage('Please enter a valid amount');
      return;
    }

    if (topupPaymentType === 'offline') {
      if (!topupOfflineMethodId) {
        setErrorMessage('Please select a payment destination');
        return;
      }
      if (!topupTransactionDate) {
        setErrorMessage('Please select a transaction date');
        return;
      }
      if (!topupProofFile) {
        setErrorMessage('Please upload payment proof for offline payment');
        return;
      }
    }

    // Compare the submitted amount against the value extracted from the proof.
    // Only applies when the extractor produced a value; manual entry without
    // a parseable proof is allowed without prompting.
    const submittedAmount = parseFloat(topupAmount);
    const extractedAmt = topupExtractedAmount != null ? parseFloat(topupExtractedAmount) : null;
    const isMismatch =
      topupPaymentType === 'offline' &&
      extractedAmt != null &&
      !Number.isNaN(extractedAmt) &&
      submittedAmount.toFixed(2) !== extractedAmt.toFixed(2);
    if (isMismatch && !confirmedMismatch) {
      setAmountMismatchDialogOpen(true);
      return;
    }

    setProcessing(true);
    try {
      const amount = submittedAmount;
      console.log(`[Payments] Submitting ${topupPaymentType} top-up request for AED ${amount}`);

      let proofUploadFailed = false;

      // Prepare the topup request payload
      const topupRequest = {
        amount: amount,
        paymentMethodId: topupOfflineMethodId || null, // Only for offline payments
        destinationBankAccountId: topupDestinationBankId || null, // Destination bank for offline payments
        transactionDate: topupPaymentType === 'offline' ? (toLocalDateString(topupTransactionDate) || null) : null,
        comment: topupPaymentType === 'online'
          ? `Online payment via ${topupPaymentMethod}`
          : `Offline payment via ${topupPaymentMethod}`,
        ...(topupPaymentType === 'online' && { paymentGateway: 'NGENIUS' }),
        // Flag the mismatch so the approvals page can highlight it. Tags are
        // Map<String,String> on the backend, so every value is stringified.
        ...(isMismatch && {
          tags: {
            amountMismatch: 'true',
            extractedAmount: extractedAmt.toFixed(2),
            submittedAmount: amount.toFixed(2),
          },
        }),
      };

      // Submit the topup request to the API
      const response = await TicketService.submitTopupRequest(topupRequest);
      console.log('[Payments] Topup request submitted successfully:', response);

      if (topupPaymentType === 'offline') {
        try {
          const topUpId = response?.id;
          if (topUpId && topupProofFile) {
            startGlobalLoader();
            await TicketService.uploadTopupProof(topUpId, topupProofFile);
          } else if (!topUpId) {
            console.warn('[Payments] Topup response missing id; skipping proof upload');
          }
        } catch (uploadErr) {
          console.error('[Payments] Proof upload failed:', uploadErr);
          proofUploadFailed = true;
          setErrorMessage('Top-up submitted, but proof upload failed. Please retry or contact support.');
        } finally {
          stopGlobalLoader();
        }
      }

      if (topupPaymentType === 'online') {
        // For online payments, redirect to payment gateway
        if (response?.paymentUrl) {
          setSuccessMessage(`Redirecting to NGenius payment gateway...`);
          window.location.href = response.paymentUrl;
          return;
        }
        // Fallback if no paymentUrl returned
        setSuccessMessage(`✅ Top-up request submitted. Please check your transaction history for status updates.`);
      } else {
        // For offline payments, show pending approval message
        const selectedInstitution = customerPaymentMethods.find(m => (m.id || m._id) === topupOfflineMethodId);
        const institutionName = selectedInstitution ? selectedInstitution.name : 'Selected Institution';
        const methodSourceLabel = topupPaymentMethod === 'bank_transfer' ? 'Bank Transfer'
          : topupPaymentMethod === 'cheque' ? 'Cheque'
          : topupPaymentMethod === 'cash_deposit' ? 'Cash Deposit' : 'Other';
        console.log(`[Payments] Offline payment submitted via ${methodSourceLabel} to ${institutionName}`);
        setSuccessMessage(`✅ Offline payment submitted! Amount: AED ${amount.toFixed(2)}\n\nStatus: PENDING APPROVAL\nPayment To: ${institutionName}\nMethod: ${methodSourceLabel}\nProof Document: ${proofUploadFailed ? 'UPLOAD FAILED' : 'UPLOADED'}\n\nThaklis admin will review and approve your payment within 24-48 hours.`);
      }

      setShowTopupDialog(false);
      setTopupAmount('');
      setTopupProofFile(null);
      setTopupTransactionDate(null);
      setTopupPaymentType('online');
      setTopupPaymentMethod('ngenius');
      setTopupOfflineMethodId('');
      setTopupExtractedAmount(null);
      setAmountMismatchDialogOpen(false);

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('[Payments] Top-up error:', error);
      setErrorMessage('Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayTicketsConfirm = async () => {
    const totalAmount = calculateTotalAmount();

    // Validation for wallet payment — compare in integer cents so
    // floating-point drift from summing decimals (e.g. 277.50 + 277.50
    // + 298.39 = 5039.939999999999) doesn't trip the check when the
    // visible totals are equal.
    if (payTicketsPaymentType === 'wallet') {
      const walletCents = Math.round(Number(walletBalance) * 100);
      const totalCents = Math.round(Number(totalAmount) * 100);
      if (walletCents < totalCents) {
        setErrorMessage(`Insufficient wallet balance! Need AED ${totalAmount.toFixed(2)}, have AED ${walletBalance.toFixed(2)}`);
        return;
      }
    }

    setProcessing(true);
    try {
      console.log(`[Payments] Processing ${payTicketsPaymentType} ticket payment for AED ${totalAmount}`);

      if (payTicketsPaymentType === 'wallet') {
        // Process wallet payment via API
        const paymentRequest = {
          ticketIds: selectedTickets,
          paymentAmount: totalAmount,
          remarks: `Wallet payment for ${selectedTickets.length} ticket(s)`
        };

        console.log('[Payments] Submitting wallet payment for tickets:', paymentRequest);
        const response = await TicketService.processWalletPayment(customerId || organizationId, paymentRequest);
        console.log('[Payments] Wallet payment successful:', response);

        // Update local balance
        const newBalance = walletBalance - totalAmount;
        setWalletBalance(newBalance);
        
        // Update payment summary tiles (don't add to totalAmountPaid — wallet-to-ticket is internal, not a new external payment)
        // Also decrement the unpaid bucket so the Unpaid tile (now driven
        // by paymentSummary) reflects the payment immediately. Server
        // refresh below replaces this with the authoritative value.
        setPaymentSummary(prev => ({
          ...prev,
          currentWalletBalance: newBalance,
          totalPaidTicketsAmount: (Number(prev.totalPaidTicketsAmount) || 0) + totalAmount,
          paidTicketsCount: (Number(prev.paidTicketsCount) || 0) + selectedTickets.length,
          totalUnpaidTicketsAmount: Math.max(0, (Number(prev.totalUnpaidTicketsAmount) || 0) - totalAmount),
          unpaidTicketsCount: Math.max(0, (Number(prev.unpaidTicketsCount) || 0) - selectedTickets.length),
        }));
        
        setSuccessMessage(`✅ Payment successful! Deducted AED ${totalAmount.toFixed(2)} from wallet for ${selectedTickets.length} ticket(s). New balance: AED ${newBalance.toFixed(2)}`);
      } else if (payTicketsPaymentType === 'online') {
        // Process online payment via NGenius payment gateway
        const paymentData = {
          ticketIds: selectedTickets
        };

        console.log('[Payments] Submitting online payment for tickets via NGenius:', paymentData);
        const { TicketPaymentAPI } = await import('../../../services/walletService');
        const response = await TicketPaymentAPI.submitOnlinePayment(customerId || organizationId, paymentData, 'NGENIUS');

        if (response?.paymentUrl) {
          setSuccessMessage(`Redirecting to NGenius payment gateway...`);
          window.location.href = response.paymentUrl;
          return;
        }
        // Fallback if no paymentUrl returned
        setSuccessMessage(`✅ Online payment initiated for ${selectedTickets.length} ticket(s). Please check your transaction history for status updates.`);
      }

      // Snapshot the just-paid IDs before clearing selection so we can strip
      // them from the visible list (the server refresh below races with the
      // user re-clicking; an optimistic local filter prevents stale re-select).
      const justPaidIds = [...selectedTickets];

      setShowPayTicketsDialog(false);
      setSelectedTickets([]);
      setPayTicketsPaymentType('wallet');
      setPayTicketsPaymentMethod('ngenius');

      // Optimistic local update so the just-paid tickets disappear immediately.
      setUnpaidTickets(prev => prev.filter(t => !justPaidIds.includes(t.ticketId)));
      setUnpaidTotal(prev => Math.max(0, prev - justPaidIds.length));
      setUnpaidTotalAmount(prev => Math.max(0, prev - totalAmount));

      // Refresh from server immediately (authoritative state). Keep the
      // success banner visible for 3s without blocking the reload.
      setUnpaidPage(0);
      loadUnpaidTickets({ page: 0 });
      // Refresh paymentSummary so the Unpaid + Processing + Paid tile
      // sections show authoritative server values — especially important
      // for online payments where tickets move into the WAITING_FOR_APPROVAL
      // bucket, not Paid.
      try {
        const refreshed = await TicketService.getPaymentSummary();
        if (refreshed) setPaymentSummary(refreshed);
      } catch (e) {
        // non-fatal — optimistic local update already applied
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('[Payments] Payment error:', error);
      setErrorMessage('Payment processing failed. Please try again.');
      // Clear selection on error too — backend state is unknown and the user
      // should re-pick from a fresh list rather than retry the same IDs.
      setSelectedTickets([]);
      loadUnpaidTickets({ page: unpaidPage });
    } finally {
      setProcessing(false);
    }
  };

  const renderTransactionStatus = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <CheckCircle sx={{ color: 'green', fontSize: 16 }} />
          <Typography sx={{ color: 'green', fontWeight: 600, fontSize: '0.9rem' }}>Completed</Typography>
        </Box>
      );
    }
    if (s === 'PENDING' || s === 'PENDING_APPROVAL') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <HourglassEmpty sx={{ color: 'orange', fontSize: 16 }} />
          <Typography sx={{ color: 'orange', fontWeight: 600, fontSize: '0.9rem' }}>Pending</Typography>
        </Box>
      );
    }
    if (s === 'REJECTED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Error sx={{ color: 'red', fontSize: 16 }} />
          <Typography sx={{ color: 'red', fontWeight: 600, fontSize: '0.9rem' }}>Rejected</Typography>
        </Box>
      );
    }
    if (s === 'FAILED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Error sx={{ color: '#d32f2f', fontSize: 16 }} />
          <Typography sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.9rem' }}>Failed</Typography>
        </Box>
      );
    }
    if (s === 'CANCELLED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Error sx={{ color: '#9e9e9e', fontSize: 16 }} />
          <Typography sx={{ color: '#9e9e9e', fontWeight: 600, fontSize: '0.9rem' }}>Cancelled</Typography>
        </Box>
      );
    }
    if (s === 'REVERSED') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Error sx={{ color: '#9e9e9e', fontSize: 16 }} />
          <Typography sx={{ color: '#9e9e9e', fontWeight: 600, fontSize: '0.9rem' }}>Reversed</Typography>
        </Box>
      );
    }
    // Fallback for any unknown status
    return (
      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
        {status ? status.replace(/_/g, ' ') : '—'}
      </Typography>
    );
  };

  const totalTicketAmount = calculateTotalAmount();
  const balanceAfterPayment = walletBalance - totalTicketAmount;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          💳 Payments Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Top-up your wallet or pay for your tickets
        </Typography>
      </Box>

      {/* Alerts */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, whiteSpace: 'pre-line' }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* Payment Summary Cards - Top Overview (Compact) */}
      <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
        {/* Card 1: Current Wallet Balance - FIRST (Most Important) */}
        <Card sx={{ p: 1.5, background: 'linear-gradient(135deg, #1a7f8f 0%, #156b7b 100%)', color: 'white', boxShadow: '0 4px 12px rgba(26, 95, 107, 0.3)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.5 }}>Current Wallet Balance</Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                {formatAmount(paymentSummary.currentWalletBalance || walletBalance)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75, mt: 0.3 }}>🏦 Available</Typography>
            </Box>
            <AccountBalance sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
          </Box>
        </Card>

        {/* Card 2: Topups — Approved / In Review / Rejected combined into one tile.
            Amounts are the raw top-up amounts (platform fee excluded). */}
        <Card sx={{ p: 1.5, background: 'linear-gradient(135deg, #1a7f8f 0%, #156b7b 100%)', color: 'white', boxShadow: '0 4px 12px rgba(26, 95, 107, 0.25)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ width: '100%' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>Topups</Typography>

              {/* Approved */}
              <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {formatAmount(paymentSummary.totalApprovedAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>(Approved)</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.approvedCount || 0}
                  </Typography>
                </Box>
              </Box>

              {/* In Review */}
              <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#42a5f5' }}>
                      {formatAmount(paymentSummary.totalPendingAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>(In Review)</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.pendingCount || 0}
                  </Typography>
                </Box>
              </Box>

              {/* Rejected */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ef5350' }}>
                      {formatAmount(paymentSummary.totalRejectedAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>(Rejected)</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.rejectedCount || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <CheckCircle sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
          </Box>
        </Card>

        {/* Card 4: Ticket Payments Summary (Paid + Unpaid) */}
        <Card sx={{ p: 1.5, background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)', color: 'white', boxShadow: '0 4px 12px rgba(26, 95, 107, 0.25)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ width: '100%' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>Ticket Payments</Typography>

              {/* Paid Section */}
              <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {formatAmount(paymentSummary.totalPaidTicketsAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                      (Paid)
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.paidTicketsCount || 0}
                  </Typography>
                </Box>
              </Box>

              {/* In Review Section — WAITING_FOR_APPROVAL: online payment
                  initiated, awaiting gateway confirmation. Separate bucket
                  from Paid (settled) and Unpaid (not yet initiated). */}
              <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#42a5f5' }}>
                      {formatAmount(paymentSummary.totalInReviewTicketsAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                      (Processing)
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.inReviewTicketsCount || 0}
                  </Typography>
                </Box>
              </Box>

              {/* Unpaid Section — reads from paymentSummary (authoritative
                  org-wide aggregate). */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff9800' }}>
                      {formatAmount(paymentSummary.totalUnpaidTicketsAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                      (Unpaid)
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                    {paymentSummary.unpaidTicketsCount || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <CreditCard sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
          </Box>
        </Card>

        {/* Card 5: Total Platform Fee Given — pass-through gateway fees the customer
            paid on top of top-ups + online ticket payments. Tracked separately; not
            part of any amount in the other tiles. */}
        <Card sx={{ p: 1.5, background: 'linear-gradient(135deg, #155566 0%, #0f4656 100%)', color: 'white', boxShadow: '0 4px 12px rgba(26, 95, 107, 0.25)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.5 }}>Total Platform Fee Given</Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                {formatAmount(paymentSummary.totalPlatformFeeGiven)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75, mt: 0.3 }}>
                💳 Gateway charges paid
              </Typography>
            </Box>
            <CreditCard sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
          </Box>
        </Card>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => {
          setTabValue(newValue);
          // Open Ticket History tab → fetch page 0 with the current filter.
          if (newValue === 2) {
            setTicketHistoryPage(0);
            loadAllTickets({ page: 0 });
          }
        }}>
          <Tab label="💰 Top-up Wallet" />
          <Tab label="💳 Pay for Tickets" />
          <Tab label="📋 Ticket History" />
          <Tab label="📜 Transaction History" />
        </Tabs>
      </Paper>

      {/* Tab 1: Top-up Wallet */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ maxWidth: 600 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Add Money to Your Wallet
          </Typography>

          <Card sx={{ mb: 3, p: 3 }}>
            {/* Amount Input */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Top-up Amount
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="Enter amount (e.g., 500)"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                onWheel={(e) => e.target.blur()} // Prevent accidental changes from mouse wheel
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Payment Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                Payment Type
              </Typography>
              <RadioGroup
                value={topupPaymentType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setTopupPaymentType(nextType);
                  setTopupPaymentMethod(nextType === 'online' ? 'ngenius' : (availablePaymentMethods[0]?.id || ''));
                  setTopupOfflineMethodId('');
                  setTopupProofFile(null);
                  setTopupTransactionDate(null);
                  setTopupProofExtractionError(null);
                  setTopupExtractedAmount(null);
                }}
              >
                <FormControlLabel value="online" control={<Radio />} label="Online Payment (Immediate)" />
                <FormControlLabel value="offline" control={<Radio />} label="Offline Payment (Pending Approval)" />
              </RadioGroup>
            </Box>

            {/* Online Payment Methods */}
            {topupPaymentType === 'online' && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={topupPaymentMethod}
                    onChange={(e) => setTopupPaymentMethod(e.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="ngenius">💳 Credit/Debit Card</MenuItem>
                    <MenuItem value="tabby" disabled>🏦 Tabby — Buy Now, Pay Later (Coming Soon)</MenuItem>
                    <MenuItem value="noqudi" disabled>📱 Noqudi (Coming Soon)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Offline Payment Methods */}
            {topupPaymentType === 'offline' && (
              <>
                {/* Step 1: Select Payment Method Type First */}
                <Box sx={{ mb: 3 }}>
                  {availablePaymentMethods.length === 0 ? (
                    <Alert severity="warning">No payment methods configured. Please contact Thaklis admin.</Alert>
                  ) : (
                    <>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                        Payment Method *
                      </Typography>
                      <FormControl fullWidth>
                      <Select
                        value={topupPaymentMethod || (availablePaymentMethods[0]?.id) || ''}
                        onChange={(e) => {
                          setTopupPaymentMethod(e.target.value);
                          // Reset bank account selection when payment method changes
                          setTopupOfflineMethodId('');
                        }}
                        label="Payment Method"
                      >
                        {availablePaymentMethods.map((method) => (
                          <MenuItem key={method.id} value={method.id}>
                            {method.name}
                          </MenuItem>
                        ))}
                      </Select>
                      </FormControl>
                    </>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                    Transaction Date *
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={topupTransactionDate}
                      onChange={(newValue) => setTopupTransactionDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Select transaction date',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* SECTION 1: Your Payment Details */}
                <Card sx={{ mb: 4, border: '1px solid rgba(6, 77, 81, 0.1)', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#064d51', mb: 2.5 }}>
                      Your Payment Details
                    </Typography>

                    {/* Step 2: Select Your Bank Account (filtered by payment method type) */}
                    <Box sx={{ mb: 3 }}>
                  {(() => {
                    // Get the selected payment method's details
                    const effectiveMethodId = topupPaymentMethod || availablePaymentMethods[0]?.id;
                    const selectedMethod = availablePaymentMethods.find(m => m.id === effectiveMethodId);
                    const selectedMethodId = selectedMethod?.id;
                    const selectedMethodName = selectedMethod?.name || availablePaymentMethods[0]?.name || topupPaymentMethod || '';

                    // Normalize strings for comparison (case-insensitive, remove spaces/underscores)
                    const normalize = (str) => {
                      return (str || '').toLowerCase().replace(/[_\s-]/g, '');
                    };
                    const normalizedSelectedName = normalize(selectedMethodName);

                    // Filter customer payment methods by the selected type
                    const filteredMethods = customerPaymentMethods.filter(m => {
                      // Try matching by normalized name
                      const byName = normalize(m.paymentMethodType) === normalizedSelectedName ||
                                     normalize(m.paymentMethod?.name) === normalizedSelectedName;
                      return byName;
                    });

                    if (filteredMethods.length === 0) {
                      return (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No bank accounts configured for {selectedMethodName}. Go to Wallet → My Payment Methods to add one.
                        </Alert>
                      );
                    }

                    return (
                      <FormControl fullWidth>
                        <InputLabel>Your Bank Account *</InputLabel>
                        <Select
                          value={topupOfflineMethodId}
                          onChange={(e) => setTopupOfflineMethodId(e.target.value)}
                          label="Your Bank Account"
                        >
                          {filteredMethods.map((method) => (
                            <MenuItem key={method.id || method._id} value={method.id || method._id}>
                              {method.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })()}
                </Box>

                    {/* File Upload */}
                    <Box sx={{ mb: 0 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                        Upload Payment Proof *
                      </Typography>
                      <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        startIcon={<CloudUpload />}
                        disabled={topupProofExtractionLoading}
                        sx={{
                          mb: 1,
                          backgroundColor: '#1a5f6b',
                          color: 'white',
                          fontWeight: 500,
                          py: 1.5,
                          '&:hover': { backgroundColor: '#0f3d47' }
                        }}
                      >
                        {topupProofExtractionLoading
                          ? 'Extracting...'
                          : (topupProofFile ? topupProofFile.name : 'Select Image/Document')}
                        <input
                          hidden
                          accept="image/*,.pdf"
                          type="file"
                          onChange={handleTopupProofFileChange}
                        />
                      </Button>
                      {topupProofExtractionLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" color="text.secondary">
                            Extracting amount and transaction date...
                          </Typography>
                        </Box>
                      )}
                      {topupProofExtractionError && (
                        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setTopupProofExtractionError(null)}>
                          {topupProofExtractionError}
                        </Alert>
                      )}
                      {topupProofFile && (
                        <Typography variant="caption" color="success.main">
                          ✓ {topupProofFile.name}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* SECTION 2: Where Payment Will Go */}
                <Card sx={{ mb: 3, border: '2px solid rgba(6, 77, 81, 0.3)', boxShadow: '0 4px 12px rgba(6, 77, 81, 0.08)' }}>
                  <CardContent sx={{ py: 2, px: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#064d51', mb: 2, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Business sx={{ color: '#064d51', fontSize: 18 }} />
                      Where Payment Will Go
                    </Typography>

                    {/* Select Destination Bank Account */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
                        Destination Bank Account (Operation) *
                      </Typography>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="destination-bank-label">Select Destination Bank</InputLabel>
                        <Select
                          labelId="destination-bank-label"
                          id="destination-bank-select"
                          value={topupDestinationBankId}
                          onChange={(e) => setTopupDestinationBankId(e.target.value)}
                          label="Select Destination Bank"
                        >
                          {thaklisBankAccounts.filter(acc => acc.isActive).map((bank) => (
                            <MenuItem key={bank.id} value={bank.id}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {bank.bankName}
                                  {bank.isPrimary && ' (Primary)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {bank.accountName}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Selected Bank Details - Sleek Confirmation Card */}
                      {thaklisBankAccounts.find(acc => acc.id === topupDestinationBankId) && (
                        <Paper sx={{ p: 1.5, bgcolor: 'linear-gradient(135deg, rgba(6, 77, 81, 0.05) 0%, rgba(6, 77, 81, 0.02) 100%)', border: '1.5px solid rgba(6, 77, 81, 0.15)', borderRadius: 1.5, mt: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 16, mt: 0.15, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#4caf50', display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                ✓ Payment will be sent to:
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#064d51', mb: 0.25, display: 'block', fontSize: '0.8rem' }}>
                                {thaklisBankAccounts.find(acc => acc.id === topupDestinationBankId)?.bankName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.15, fontSize: '0.7rem' }}>
                                Account: {thaklisBankAccounts.find(acc => acc.id === topupDestinationBankId)?.accountNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                IBAN: {thaklisBankAccounts.find(acc => acc.id === topupDestinationBankId)?.iban}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Alert severity="info" sx={{ mb: 2, py: 1, fontSize: '0.85rem' }}>
                  Your payment will be reviewed within 24-48 hours.
                </Alert>
              </>
            )}

            {topupAmount && (
              <Box sx={{ background: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Current: AED {walletBalance.toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#667eea' }}>
                  + AED {parseFloat(topupAmount || 0).toFixed(2)}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'green' }}>
                  {topupPaymentType === 'online' ? 'New' : 'Pending'}: AED {(walletBalance + parseFloat(topupAmount || 0)).toFixed(2)}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={() => setShowTopupDialog(true)}
              disabled={
                !topupAmount ||
                parseFloat(topupAmount) <= 0 ||
                (topupPaymentType === 'offline' && (!topupOfflineMethodId || !topupProofFile))
              }
            >
              Proceed to {topupPaymentType === 'online' ? 'Pay' : 'Submit'}
            </Button>
          </Card>
        </Box>
      </TabPanel>

      {/* Tab 2: Pay for Tickets */}
      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Your Unpaid Tickets ({unpaidTotal})
            </Typography>

            {unpaidTotal === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                ✅ All tickets are paid! No unpaid tickets found.
              </Alert>
            ) : (
              <>
                {/* Header with Selection Summary on the same line */}
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {unpaidTotal} unpaid ticket{unpaidTotal !== 1 ? 's' : ''} found
                  </Typography>

                  {selectedTickets.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ color: '#333', fontWeight: '600', fontSize: '0.9rem' }}>
                        {selectedTickets.length} selected • AED {totalTicketAmount.toFixed(2)}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => setShowPayTicketsDialog(true)}
                      >
                        Pay
                      </Button>
                    </Box>
                  )}
                </Box>

                <TableContainer sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#064d51' }}>
                        <TableCell padding="checkbox" sx={{ color: 'white', backgroundColor: '#064d51' }}>
                          <Checkbox
                            sx={{
                              color: 'white',
                              '&.Mui-checked': { color: 'white' },
                              '&.MuiCheckbox-indeterminate': { color: 'white' },
                            }}
                            checked={unpaidTickets.length > 0 && selectedTickets.length === unpaidTickets.length}
                            indeterminate={selectedTickets.length > 0 && selectedTickets.length < unpaidTickets.length}
                            onChange={handleSelectAllUnpaidTickets}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Ticket ID</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Customer Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Service</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Created Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Status</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Amount (AED)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unpaidTickets.map((ticket, index) => (
                        <TableRow
                          key={ticket.ticketId}
                          onClick={() => handleTicketToggle(ticket.ticketId)}
                          sx={{
                            backgroundColor: selectedTickets.includes(ticket.ticketId) ? '#f3f4ff' : (index % 2 === 0 ? '#fafafa' : 'white'),
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f0f0f0' },
                            borderBottom: '1px solid #e0e0e0'
                          }}
                        >
                          <TableCell sx={{ padding: '12px 8px' }} onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTickets.includes(ticket.ticketId)}
                              onChange={() => handleTicketToggle(ticket.ticketId)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: '12px 8px', fontWeight: 600 }}>
                            {ticket.orgSequenceNumber || '—'}
                          </TableCell>
                          <TableCell sx={{ padding: '12px 8px' }}>
                            {ticket.customerName || '—'}
                          </TableCell>
                          <TableCell sx={{ padding: '12px 8px' }}>
                            {ticket.serviceName || ticket.description || '—'}
                          </TableCell>
                          <TableCell sx={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                            {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString('en-GB') : '\u2014'}
                          </TableCell>
                          <TableCell sx={{ padding: '12px 8px' }}>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '20px',
                                backgroundColor: 'rgba(255, 165, 0, 0.15)',
                                color: '#ff9800',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase'
                              }}
                            >
                              UNPAID
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ padding: '12px 8px', fontWeight: 600, color: '#667eea' }}>
                            AED {(ticket.totalCost || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Server-side pagination — selection is reset on page/size
                    change since `unpaidTickets` only holds the current slice
                    and the selected-amount sum reads from it. */}
                <TablePagination
                  component="div"
                  count={unpaidTotal}
                  page={unpaidPage}
                  onPageChange={(_, newPage) => {
                    setUnpaidPage(newPage);
                    setSelectedTickets([]);
                    loadUnpaidTickets({ page: newPage });
                  }}
                  rowsPerPage={unpaidPageSize}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    setUnpaidPageSize(next);
                    setUnpaidPage(0);
                    setSelectedTickets([]);
                    loadUnpaidTickets({ page: 0, pageSize: next });
                  }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              </>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Tab 3: Ticket History */}
      <TabPanel value={tabValue} index={2}>
        <Box>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontSize: '1.1rem' }}>
            Your Ticket History
          </Typography>

          {ticketHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : allTickets.length === 0 ? (
            <Alert severity="info">
              No tickets found.
            </Alert>
          ) : (
            <>
              {/* Status Filter */}
              <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['ALL', 'PAID', 'UNPAID', 'WAITING_FOR_APPROVAL', 'PAYMENT_REJECTED'].map((status) => {
                    const getStatusStyle = (s) => {
                      const isActive = ticketStatusFilter === s;
                      const baseStyle = {
                        px: 1.5,
                        py: 0.6,
                        borderRadius: '20px',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1.5px solid',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase'
                      };

                      const statusColors = {
                        ALL: isActive
                          ? { bg: '#064d51', color: 'white', borderColor: '#064d51' }
                          : { bg: 'transparent', color: '#064d51', borderColor: '#064d51' },
                        PAID: isActive
                          ? { bg: '#4caf50', color: 'white', borderColor: '#4caf50' }
                          : { bg: 'transparent', color: '#4caf50', borderColor: '#4caf50' },
                        UNPAID: isActive
                          ? { bg: '#ff9800', color: 'white', borderColor: '#ff9800' }
                          : { bg: 'transparent', color: '#ff9800', borderColor: '#ff9800' },
                        WAITING_FOR_APPROVAL: isActive
                          ? { bg: '#ffc107', color: 'white', borderColor: '#ffc107' }
                          : { bg: 'transparent', color: '#ffc107', borderColor: '#ffc107' },
                        PAYMENT_REJECTED: isActive
                          ? { bg: '#f44336', color: 'white', borderColor: '#f44336' }
                          : { bg: 'transparent', color: '#f44336', borderColor: '#f44336' }
                      };

                      return {
                        ...baseStyle,
                        ...statusColors[s],
                        '&:hover': {
                          boxShadow: `0 4px 8px rgba(${s === 'PAID' ? '76,175,80' : s === 'UNPAID' ? '255,152,0' : s === 'WAITING_FOR_APPROVAL' ? '255,193,7' : s === 'PAYMENT_REJECTED' ? '244,67,54' : '6,77,81'},0.3)`
                        }
                      };
                    };

                    const getLabel = (s) => {
                      const labels = {
                        ALL: 'All Tickets',
                        PAID: '✓ Paid',
                        UNPAID: '⏳ Unpaid',
                        WAITING_FOR_APPROVAL: '⌛ Pending',
                        PAYMENT_REJECTED: '✗ Rejected'
                      };
                      return labels[s];
                    };

                    return (
                      <Box
                        key={status}
                        component="button"
                        onClick={() => {
                          setTicketStatusFilter(status);
                          setTicketHistoryPage(0);
                          // Refetch with new server-side filter on page 0.
                          loadAllTickets({ page: 0, status });
                        }}
                        sx={getStatusStyle(status)}
                      >
                        {getLabel(status)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Results summary — driven by server total, not the page slice. */}
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Showing {ticketHistoryTotal === 0 ? 0 : ticketHistoryPage * ticketHistoryPageSize + 1}-{Math.min((ticketHistoryPage + 1) * ticketHistoryPageSize, ticketHistoryTotal)} of {ticketHistoryTotal} ticket{ticketHistoryTotal !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {filteredTickets.length === 0 ? (
                <Alert severity="info">
                  No tickets found with the selected filter.
                </Alert>
              ) : (
                <>
                  <TableContainer sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#064d51' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Ticket ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Customer Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Service</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Created Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Status</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600, padding: '12px 8px' }}>Amount (AED)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTickets.map((ticket, index) => {
                    // Determine status color based on payment status
                    const getStatusColor = (status) => {
                      switch(status) {
                        case 'PAID':
                          return { bg: 'rgba(76, 175, 80, 0.15)', color: '#4caf50' };
                        case 'UNPAID':
                          return { bg: 'rgba(255, 165, 0, 0.15)', color: '#ff9800' };
                        case 'WAITING_FOR_APPROVAL':
                          return { bg: 'rgba(255, 193, 7, 0.15)', color: '#ffc107' };
                        case 'PAYMENT_REJECTED':
                          return { bg: 'rgba(244, 67, 54, 0.15)', color: '#f44336' };
                        default:
                          return { bg: 'rgba(158, 158, 158, 0.15)', color: '#9e9e9e' };
                      }
                    };

                    const statusColor = getStatusColor(ticket.paymentStatus || 'UNPAID');
                    const statusLabel = (ticket.paymentStatus || 'UNPAID')
                      .split('_')
                      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                      .join(' ');

                    return (
                      <TableRow
                        key={ticket.ticketId}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                          borderBottom: '1px solid #e0e0e0'
                        }}
                      >
                        <TableCell sx={{ padding: '12px 8px', fontWeight: 600 }}>
                          {ticket.orgSequenceNumber || '—'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px 8px' }}>
                          {ticket.customerName || '—'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px 8px' }}>
                          {ticket.serviceName || ticket.description || '—'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                          {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString('en-GB') : '—'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px 8px' }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '20px',
                              backgroundColor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase'
                            }}
                          >
                            {statusLabel}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ padding: '12px 8px', fontWeight: 600, color: '#667eea' }}>
                          AED {(ticket.totalCost || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

                  {/* Server-side pagination with editable page size. */}
                  <TablePagination
                    component="div"
                    count={ticketHistoryTotal}
                    page={ticketHistoryPage}
                    onPageChange={(_, newPage) => {
                      setTicketHistoryPage(newPage);
                      loadAllTickets({ page: newPage });
                    }}
                    rowsPerPage={ticketHistoryPageSize}
                    onRowsPerPageChange={(e) => {
                      const next = parseInt(e.target.value, 10);
                      setTicketHistoryPageSize(next);
                      setTicketHistoryPage(0);
                      loadAllTickets({ page: 0, pageSize: next });
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </TabPanel>

      {/* Tab 4: Transaction History with Sub-Tabs */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
            Transaction History
          </Typography>

          {/* Sub-Tabs for Transaction History */}
          <Tabs
            value={transactionHistorySubTab}
            onChange={(e, newValue) => setTransactionHistorySubTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="💰 Top-ups" />
            <Tab label="💳 Payments to Thaklis" />
          </Tabs>

          {/* Sub-Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Sub-Tab 1: Top-ups */}
              {transactionHistorySubTab === 0 && (
                <Box>
                  {txnTopupsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : txnTopups.length === 0 ? (
                    <Alert severity="info">No top-up transactions found.</Alert>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Transaction #</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Date</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Amount</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Source</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Status</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {txnTopups.map((tx, index) => (
                            <tr key={tx.id} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#667eea' }}>
                                  {tx.transactionNumber || tx.id}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {tx.createdDate ? new Date(tx.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontWeight: 600, color: '#00897B', fontSize: '0.9rem' }}>
                                  AED {Number(tx.amount || 0).toFixed(2)}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {tx.paymentSource === 'WALLET' ? 'Wallet' : tx.paymentSource || 'Wallet'}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                {renderTransactionStatus(tx.status)}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography color="textSecondary" sx={{ fontSize: '0.9rem' }}>{tx.description || ''}</Typography>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Box>
              )}

              {/* Sub-Tab 2: Payments to Thaklis */}
              {transactionHistorySubTab === 1 && (
                <Box>
                  {txnTicketPaymentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : txnTicketPayments.length === 0 ? (
                    <Alert severity="info">No payment transactions found.</Alert>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Transaction #</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Date</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Amount</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Source</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Status</th>
                            <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, fontSize: '0.93rem' }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {txnTicketPayments.map((tx, index) => (
                            <tr key={tx.id} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#667eea' }}>
                                  {tx.transactionNumber || tx.id}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {tx.createdDate ? new Date(tx.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontWeight: 600, color: '#D32F2F', fontSize: '0.9rem' }}>
                                  AED {Number(tx.amount || 0).toFixed(2)}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {tx.paymentSource === 'WALLET' ? 'Wallet' : tx.paymentSource || '—'}
                                </Typography>
                              </td>
                              <td style={{ padding: '10px' }}>
                                {renderTransactionStatus(tx.status)}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <Typography color="textSecondary" sx={{ fontSize: '0.9rem' }}>{tx.description || ''}</Typography>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
        </Box>
      </TabPanel>

      {/* Top-up Confirmation Dialog */}
      <Dialog
        open={showTopupDialog}
        onClose={(_, reason) => {
          if (processing) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setShowTopupDialog(false);
        }}
        disableEscapeKeyDown={processing}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.3rem', pb: 1 }}>Confirm Top-up</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1.5 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
              Amount to add to wallet
            </Typography>
            <Typography sx={{ fontWeight: 'bold', color: '#667eea', mb: 2, fontSize: '1.8rem' }}>
              AED {parseFloat(topupAmount || 0).toFixed(2)}
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                Payment Type
              </Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                {topupPaymentType === 'online' ? 'Online' : 'Offline (Pending Approval)'}
              </Typography>
            </Box>

            {topupPaymentType === 'online' && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                  Payment Method
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                  {topupPaymentMethod === 'ngenius' && '💳 Credit/Debit Card'}
                  {topupPaymentMethod === 'tabby' && '🏦 Tabby'}
                  {topupPaymentMethod === 'noqudi' && '📱 Noqudi'}
                </Typography>
              </Box>
            )}

            {topupPaymentType === 'offline' && (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                    Financial Institution
                  </Typography>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    {customerPaymentMethods.find(m => (m.id || m._id) === topupOfflineMethodId)?.name || 'Selected Institution'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                    Payment Method Source
                  </Typography>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    {topupPaymentMethod === 'bank_transfer' && '🏦 Bank Transfer'}
                    {topupPaymentMethod === 'cheque' && '📄 Cheque'}
                    {topupPaymentMethod === 'cash_deposit' && '💵 Cash Deposit'}
                    {topupPaymentMethod === 'other' && '📋 Other'}
                  </Typography>
                </Box>
              </>
            )}

            <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
              {topupPaymentType === 'online'
                ? 'Your wallet will be credited immediately after successful payment.'
                : 'Your payment will be reviewed by Thaklis admin within 24-48 hours and then reflected in your wallet.'}
            </Alert>

            {topupPaymentType === 'online' && computePlatformFee(topupAmount) > 0 && (
              <Box sx={{ background: '#fff8e1', p: 1.5, borderRadius: 1, mb: 1.5, border: '1px solid #ffe0a3' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.85rem' }}>Wallet Top-up:</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>AED {parseFloat(topupAmount || 0).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.85rem' }}>Platform Fee:</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>+ AED {computePlatformFee(topupAmount).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Total to Pay:</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a7f8f' }}>
                    AED {(parseFloat(topupAmount || 0) + computePlatformFee(topupAmount)).toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  The platform fee is charged by the gateway on top of your top-up.
                </Typography>
              </Box>
            )}

            <Box sx={{ background: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>Wallet Balance Before:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>AED {walletBalance.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.85rem' }}>Wallet Balance After {topupPaymentType === 'offline' ? '(Pending)' : ''}:</Typography>
                <Typography sx={{ fontWeight: 'bold', color: 'green', fontSize: '0.85rem' }}>
                  AED {(walletBalance + parseFloat(topupAmount || 0)).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTopupDialog(false)} disabled={processing}>Cancel</Button>
          <Button
            onClick={() => handleTopupConfirm(false)}
            variant="contained"
            color="primary"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
        {processing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
              Processing top-up… please wait
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* Amount Mismatch Confirmation Dialog
          Shown when the user-entered amount differs from the amount auto-extracted
          from the uploaded payment proof. On Continue, the topup is submitted with
          tags so admins can spot it on the approvals page. */}
      <Dialog
        open={amountMismatchDialogOpen}
        onClose={(_, reason) => {
          if (processing) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setAmountMismatchDialogOpen(false);
        }}
        disableEscapeKeyDown={processing}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.1rem', color: '#d32f2f' }}>
          Amount Doesn't Match Proof
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The amount you entered differs from the amount detected on the uploaded payment proof.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: '0.9rem' }}>Extracted from proof:</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
              AED {topupExtractedAmount != null ? parseFloat(topupExtractedAmount).toFixed(2) : '—'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: '0.9rem' }}>You're submitting:</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#d32f2f' }}>
              AED {topupAmount ? parseFloat(topupAmount).toFixed(2) : '—'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
            If you continue, this top-up will be flagged for the admin's attention during approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmountMismatchDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setAmountMismatchDialogOpen(false);
              handleTopupConfirm(true);
            }}
            variant="contained"
            color="warning"
            disabled={processing}
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay Tickets Dialog - Payment Method Selection and Confirmation */}
      <Dialog
        open={showPayTicketsDialog}
        onClose={(_, reason) => {
          if (processing) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setShowPayTicketsDialog(false);
        }}
        disableEscapeKeyDown={processing}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.3rem', pb: 1 }}>Make Payment for Tickets</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            {/* Two Payment Options - Radio Buttons */}
            <Box sx={{ mb: 2.5 }}>
              <RadioGroup
                value={payTicketsPaymentType}
                onChange={(e) => {
                  setPayTicketsPaymentType(e.target.value);
                  if (e.target.value === 'wallet') {
                    setPayTicketsPaymentMethod('');
                  } else if (e.target.value === 'online') {
                    setPayTicketsPaymentMethod('ngenius');
                  }
                }}
              >
                {/* Option 1: Wallet Payment - with balance display */}
                <Box sx={{ mb: 1.5, p: 1.5, border: payTicketsPaymentType === 'wallet' ? '2px solid #1a7f8f' : '1px solid #ddd', borderRadius: 1, cursor: 'pointer', backgroundColor: payTicketsPaymentType === 'wallet' ? '#f0f9fa' : 'transparent', transition: 'all 0.2s', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Radio value="wallet" sx={{ mt: 0.5, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>💰 Wallet Payment</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 0.1 }}>
                          {walletBalance >= totalTicketAmount
                            ? `✓ Pay full amount`
                            : `Insufficient balance`
                          }
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: walletBalance >= totalTicketAmount ? '#4caf50' : '#ff9800', textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        AED {walletBalance.toFixed(2)}<br />Available
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Option 2: Online Payment (Immediate Credit) */}
                <Box sx={{ mb: 1.5, p: 1.5, border: payTicketsPaymentType === 'online' ? '2px solid #1a7f8f' : '1px solid #ddd', borderRadius: 1, cursor: 'pointer', backgroundColor: payTicketsPaymentType === 'online' ? '#f0f9fa' : 'transparent', transition: 'all 0.2s' }}>
                  <FormControlLabel
                    value="online"
                    control={<Radio />}
                    label={
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>🏬 Online Payment</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>Credit/Debit Card (immediate credit)</Typography>
                      </Box>
                    }
                    fullWidth
                  />
                </Box>
              </RadioGroup>
            </Box>

            {/* Online Payment Methods - Show when Online Payment is selected */}
            {payTicketsPaymentType === 'online' && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 1.5 }}>Payment Method</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Payment Method *</InputLabel>
                  <Select
                    value={payTicketsPaymentMethod || 'ngenius'}
                    onChange={(e) => setPayTicketsPaymentMethod(e.target.value)}
                    label="Select Payment Method"
                  >
                    <MenuItem value="ngenius">
                      💳 Credit/Debit Card
                    </MenuItem>
                    <MenuItem value="tabby" disabled>
                      🏬 Tabby — Buy Now, Pay Later (Coming Soon)
                    </MenuItem>
                    <MenuItem value="noqudi" disabled>
                      📱 Noqudi (Coming Soon)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}


            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
              Payment Summary
            </Typography>

            <Box sx={{ background: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>Selected Tickets:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{selectedTickets.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Amount:</Typography>
                <Typography sx={{ fontWeight: 'bold', color: '#667eea', fontSize: '1.1rem' }}>
                  AED {totalTicketAmount.toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>Payment Type:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                  {payTicketsPaymentType === 'wallet' ? 'Wallet' : 'Online'}
                </Typography>
              </Box>

              {payTicketsPaymentType === 'wallet' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>Current Balance:</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>AED {walletBalance.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>After Payment:</Typography>
                    <Typography sx={{ fontWeight: 'bold', color: 'green', fontSize: '0.85rem' }}>
                      AED {balanceAfterPayment.toFixed(2)}
                    </Typography>
                  </Box>
                </>
              )}

              {payTicketsPaymentType === 'online' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>Payment Gateway:</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                      {payTicketsPaymentMethod === 'ngenius' ? 'Credit/Debit Card' : payTicketsPaymentMethod === 'tabby' ? 'Tabby' : 'Noqudi'}
                    </Typography>
                  </Box>
                  {computePlatformFee(totalTicketAmount) > 0 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography sx={{ fontSize: '0.85rem' }}>Platform Fee:</Typography>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                          + AED {computePlatformFee(totalTicketAmount).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Total to Pay:</Typography>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a7f8f' }}>
                          AED {(totalTicketAmount + computePlatformFee(totalTicketAmount)).toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                        The platform fee is charged by the gateway on top of the ticket amount.
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>

            <Alert severity="info" sx={{ fontSize: '0.85rem', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
              {payTicketsPaymentType === 'wallet'
                ? 'Selected tickets will be marked as PAID immediately.'
                : 'You will be redirected to the payment gateway to complete the transaction.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPayTicketsDialog(false)} disabled={processing}>Cancel</Button>
          <Button
            onClick={handlePayTicketsConfirm}
            variant="contained"
            color="primary"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
        {processing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
              Processing payment… please wait
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}

export default Payments;
